import API from '@aws-amplify/api';
import Auth from '@aws-amplify/auth';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// TODO Removed for 1.0.0, replace once Spanish translations are finished.
// import LanguageSelector from '../shared/LanguageSelector';
import { ProtectedRouteContext } from '../Context';
import Panel from '../shared/Panel';
import PanelSection from '../shared/PanelSection';
import LabeledInput from '../shared/LabeledInput';
import ChangePassword from '../authentication/ChangePassword';
import Form from '../shared/Form';
import View from '../shared/View';
import Email from '../shared/Email';
import Name from '../shared/Name';
import Phone from '../shared/Phone';

import './UserSettings.scss';
import { Button } from '@mui/material';

function UserSettings(props) {
    const [t] = useTranslation();
    const { displayModal, user, userSettings, updateUserSettings } = useContext(ProtectedRouteContext);
    const [forceValidation, setForceValidation] = React.useState(false);
    const [formState, setFormState] = React.useState();
    const [doingAjax, setDoingAjax] = React.useState(true);
    const [ajaxErrorMessage] = React.useState(t('General.AjaxError'));
    const formRefs = useRef([]);

    function displayChangePassword(toggle) {
        setDoingAjax(toggle);
        let _extraButtonsJSX = [
            <Button
                color="secondary"
                onClick={() => displayChangePassword(false)}
            >
                {t('UserSettings.CancelChangePasswordButtonText')}
            </Button>,
        ];
        let param2 = toggle
            ? () => (
                  <div className="change-password-modal">
                      <ChangePassword
                          callback={handleNewPassword}
                          usingCurrentPassword={true}
                          nextButton={false}
                          extraButtons={_extraButtonsJSX}
                      />
                  </div>
              )
            : null;
        displayModal(toggle, param2);
    }

    const handleNewPassword = (currentPwd, newPwd) => {
        setDoingAjax(true);
        Auth.changePassword(
            // Some derivation of this
            /*user,
            pwd,
            user.challengeParam.userAttributes
            */
            user,
            currentPwd,
            newPwd,
            {
                given_name: 'Tucker',
                family_name: 'Brown',
            } //TODO must get these from existing user
        )
            .then((loggedInUser) => {
                if (loggedInUser && !loggedInUser.challengeName) {
                    // Password reset successful. Redirect to Dashboard
                    toast.success(t('Authentication.PasswordChanged'));
                }
                setDoingAjax(false);
                displayChangePassword(false);
            })
            .catch((e) => {
                switch (e.code) {
                    case 'LimitExceededException':
                        toast.error(t('Authentication.AttemptsExceeded'));
                        break;
                    case 'NotAuthorizedException':
                        toast.error(t('Authentication.InvalidCredentials'));
                        break;
                    default:
                        toast.error(t('General.AjaxError'));
                        break;
                }
                setDoingAjax(false);
            });
    };

    const onValidSave = () => {
        (async () => {
            try {
                let newData = {
                    body: {
                        FirstName: formState['FirstName'],
                        LastName: formState['LastName'],
                        Notifications: {
                            Email: formState['Notifications.Email'],
                            Sms: formState['PhoneNumber'],
                        },
                    },
                };
                await API.patch('ShipyardAPI', '/usersettings', newData);
                toast.success(t('General.ChangesSaved'));
                setDoingAjax(false);
            } catch (e) {
                toast.error(ajaxErrorMessage);
                setDoingAjax(false);
            }
        })();
    };

    //Form State
    const handleSave = (newFormState = {}) => {
        updateUserSettings(Object.assign(userSettings, newFormState));
        setFormState(newFormState);
        setDoingAjax(true);
        setForceValidation(true);
    };

    const addToRefs = (el) => {
        if (el && !formRefs.current.includes(el)) {
            formRefs.current.push(el);
        }
    };

    useEffect(() => {
        if (userSettings.Retrieved && typeof formState === 'undefined') {
            setFormState(userSettings);
            setDoingAjax(false);
        }
    }, [userSettings.Retrieved, formState, userSettings]);

    let viewTitle = (
        <h2>
            <span>{t('UserSettings.HeaderMy')}</span> <span>{t('UserSettings.HeaderInfo')}</span>
        </h2>
    );

    return (
        <View
            viewTitle={viewTitle}
            doingAjax={doingAjax}
            name="user-settings"
        >
            <Form
                ajaxErrorMessage={ajaxErrorMessage}
                onValidSave={onValidSave}
                refs={formRefs.current}
                onInvalidSave={() => {
                    setDoingAjax(false);
                }}
                primaryButton={true}
                primaryButtonText={t('UserSettings.SaveButtonText')}
                primaryButtonInteractiveText={t('General.InteractiveSave')}
                onUserInteract={{ handleSave: handleSave, handleForm: setFormState }}
            >
                <Panel>
                    <PanelSection
                        classes={['personal-info']}
                        headerText={t('UserSettings.PersonalSectionTitle')}
                    >
                        <div className="same-row">
                            <Name
                                value={formState?.FirstName ? formState.FirstName : ''}
                                formKey={'FirstName'}
                                validators={[
                                    {
                                        rgx: /.+/,
                                        failText: t('UserSettings.EnterFirstNameValid'),
                                    },
                                ]}
                                ref={addToRefs}
                                forceValidation={forceValidation}
                                labelText={t('UserSettings.FirstNameLabel')}
                                placeholder={t('UserSettings.EnterFirstName')}
                            />
                            <Name
                                value={formState?.LastName ? formState.LastName : ''}
                                formKey={'LastName'}
                                validators={[
                                    {
                                        rgx: /.+/,
                                        failText: t('UserSettings.EnterLastNameValid'),
                                    },
                                ]}
                                initialValue=""
                                ref={addToRefs}
                                forceValidation={forceValidation}
                                labelText={t('UserSettings.LastNameLabel')}
                                placeholder={t('UserSettings.EnterLastName')}
                            />
                        </div>
                        <Email
                            value={formState && formState['Notifications.Email'] ? formState['Notifications.Email'] : ''}
                            formKey={'Notifications.Email'}
                            ref={addToRefs}
                            forceValidation={forceValidation}
                        />
                        <Phone
                            value={formState?.PhoneNumber ? formState.PhoneNumber : ''}
                            formKey={'PhoneNumber'}
                            ref={addToRefs}
                            forceValidation={forceValidation}
                        />
                        <div className="language-container">
                            {/* 
                                TODO Removed for 1.0.0 Release, replace once Spanish Translations are finished.
                                <LanguageSelector
                                    id="lslsl"
                                />
                            */}
                        </div>
                    </PanelSection>
                    <PanelSection headerText={t('UserSettings.AccountSectionTitle')}>
                        <LabeledInput
                            value={user.attributes.email}
                            formKey={'UserName'}
                            labelText={t('UserSettings.UserNameLabel')}
                            placeholder={t('UserSettings.EnterUserName')}
                            ref={addToRefs}
                            disabled={true}
                        />
                        <LabeledInput
                            value={Array(8).fill('*').join('')}
                            labelText={t('Authentication.Password')}
                            placeholder={t('Authentication.EnterPassword')}
                            disabled={true}
                        />
                        <Button
                            className="change-password-button"
                            onClick={() => displayChangePassword(true)}
                            type="secondary"
                        >
                            {t('UserSettings.ChangePasswordButtonText')}
                        </Button>
                        <div style={{ height: '350px' }} />
                    </PanelSection>
                </Panel>
            </Form>
        </View>
    );
}

export default UserSettings;

//TODO replace usage of createRef with useRef Hook
